import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import type * as React from 'react';

/**
 * How wide the page lets itself be. `default` takes whatever `AppMain` leaves — the column shared with
 * the header and the section tabs; the three others narrow below it, never past it.
 */
export type PageWidth = 'default' | 'sm' | 'md' | 'xl';
type ScrollY = 'auto' | 'hidden' | 'scroll' | 'visible' | 'clip';

const MAX_WIDTH_BY_WIDTH: Record<PageWidth, string> = {
	default: '100%',
	sm: theme.layout.pageWidths.sm,
	md: theme.layout.pageWidths.md,
	xl: theme.layout.pageWidths.xl,
};

const containerStyle = (scrollY?: ScrollY) => css`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	flex: 1;
	min-height: 0;
	width: 100%;
	margin: 0 auto;
	overflow-y: ${scrollY ?? 'auto'};
	padding: ${scrollY ? '0 1rem' : undefined}
`;

/**
 * Kept out of `containerStyle` so the declaration that varies is its own rule: `width` is the only
 * thing a caller changes here, and a single-property style reads as such in the devtools.
 */
const widthStyle = (width: PageWidth) => css`
	max-width: ${MAX_WIDTH_BY_WIDTH[width]};
`;

type Props = {
	/** Narrows the page below the shell's column. Defaults to taking the full column. */
	readonly width?: PageWidth;
	readonly className?: string;
	readonly children: React.ReactNode;
	scrollY?: ScrollY;
};

/**
 * The root a page mounts inside `AppMain`: the vertical rhythm between its blocks, and how wide it
 * lets itself be.
 *
 * Width is declared here, by the page, rather than resolved from the route or pushed up through a
 * signal: it is read where the question is asked, it costs no state, and it cannot go stale when a
 * path is renamed.
 */
const PageContainer: React.FC<Props> = ({ children, className, width = 'default', scrollY }) => {
	return (
		<div css={[containerStyle(scrollY), widthStyle(width)]} className={className}>
			{children}
		</div>
	);
};

export default PageContainer;
